import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  Baby,
  BookOpen,
  Briefcase,
  Flower2,
  Heart,
  HeartOff,
  ListTree,
  Pencil,
  ScrollText,
  Sparkles,
  type LucideIcon,
} from 'lucide-react';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { useConfirm } from '@/components/common/ConfirmDialog';
import { useAuthStore } from '@/store/useAuthStore';
import { usePerson } from '@/hooks/usePersons';
import { QUERY_KEYS } from '@/api/queryKeys';
import {
  getBiography,
  getChronicle,
  upsertBiography,
  type ChronicleEvent,
  type ChronicleEventType,
} from '@/api/career.api';

// ─── Query Keys (Lebenslauf & Chronik) ──────────────────────────────────────
// Aliasse auf das zentrale Register (queryKeys.ts) – dort definiert, damit
// auch die CareerSection im Personen-Panel dieselben Keys invalidieren kann.

export const CAREER_QUERY_KEYS = {
  /** Basis-Key: invalidiert Liste UND alle Detail-Texte */
  biographies: QUERY_KEYS.career.biographies,
  /** Alle Personen mit gepflegtem Lebenslauf-Text */
  biographyList: QUERY_KEYS.career.biographiesList,
  /** Erzähltext einer Person */
  biography: QUERY_KEYS.career.biography,
  /** Generierte Chronik einer Person */
  chronicle: QUERY_KEYS.career.chronicle,
};

// ─── Event-Typ → Icon ────────────────────────────────────────────────────────

const EVENT_ICONS: Record<ChronicleEventType, LucideIcon> = {
  birth: Sparkles,
  occupation: Briefcase,
  marriage: Heart,
  separation: HeartOff,
  child: Baby,
  death: Flower2,
};

const EVENT_ICON_STYLES: Record<ChronicleEventType, string> = {
  birth: 'bg-amber-50 text-amber-600 border-amber-200',
  occupation: 'bg-blue-50 text-blue-600 border-blue-200',
  marriage: 'bg-rose-50 text-rose-600 border-rose-200',
  separation: 'bg-slate-100 text-slate-500 border-slate-200',
  child: 'bg-emerald-50 text-emerald-600 border-emerald-200',
  death: 'bg-slate-100 text-slate-600 border-slate-300',
};

// ─── Chronik-Zeitleiste ──────────────────────────────────────────────────────

function ChronicleTimeline({ events }: { events: ChronicleEvent[] }) {
  if (events.length === 0) {
    return (
      <p className="text-sm text-slate-500">
        Für diese Person sind noch keine Ereignisse erfasst.
      </p>
    );
  }

  return (
    <ol>
      {events.map((ev, i) => {
        const Icon = EVENT_ICONS[ev.type];
        const isLast = i === events.length - 1;
        return (
          <li key={`${ev.type}-${i}`} className="flex gap-3">
            {/* Jahr / Datumstext links */}
            <div className="w-24 flex-shrink-0 pt-1 text-right sm:w-28">
              <p className="text-sm font-semibold text-slate-700">{ev.year ?? '—'}</p>
              {/* Freitext nur zeigen, wenn er mehr sagt als die Jahreszahl links */}
              {ev.dateText && ev.dateText !== String(ev.year ?? '') && (
                <p className="text-xs text-slate-400">{ev.dateText}</p>
              )}
            </div>

            {/* Icon + Verbindungslinie */}
            <div className="flex flex-col items-center">
              <span
                className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border ${EVENT_ICON_STYLES[ev.type]}`}
              >
                <Icon className="h-4 w-4" aria-hidden="true" />
              </span>
              {!isLast && <span className="w-px flex-1 bg-slate-200" aria-hidden="true" />}
            </div>

            {/* Titel + Detail rechts */}
            <div className={`min-w-0 flex-1 pt-1 ${isLast ? '' : 'pb-6'}`}>
              {ev.relatedPersonId ? (
                <Link
                  to={`/?person=${ev.relatedPersonId}`}
                  className="text-sm font-medium text-blue-700 hover:underline"
                  title="Im Stammbaum zeigen"
                >
                  {ev.title}
                </Link>
              ) : (
                <p className="text-sm font-medium text-slate-800">{ev.title}</p>
              )}
              {ev.detail && <p className="mt-0.5 text-sm text-slate-500">{ev.detail}</p>}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

// ─── BiographyView ───────────────────────────────────────────────────────────
// Detailansicht eines Lebenslaufs: Kopf mit Lebensdaten, generierte Chronik
// als Zeitleiste und der gepflegte Erzähltext (mit persons.manage editierbar).

interface BiographyViewProps {
  personId: string;
  /** Zurück zur Lebenslauf-Liste */
  onBack: () => void;
}

export function BiographyView({ personId, onBack }: BiographyViewProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const canManage = useAuthStore((s) => s.hasPermission('persons.manage'));

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  const personQuery = usePerson(personId);
  const { data: person, isError: personError } = personQuery;

  const chronicleQuery = useQuery({
    queryKey: CAREER_QUERY_KEYS.chronicle(personId),
    queryFn: () => getChronicle(personId),
  });
  const { data: chronicle, isLoading: chronicleLoading, isError: chronicleError } = chronicleQuery;

  const { data: biography, isLoading: biographyLoading } = useQuery({
    queryKey: CAREER_QUERY_KEYS.biography(personId),
    queryFn: () => getBiography(personId),
  });

  const saveMutation = useMutation({
    mutationFn: (text: string) => upsertBiography(personId, text),
    onSuccess: (result) => {
      toast.success(result ? 'Lebenslauf gespeichert.' : 'Lebenslauf-Text gelöscht.');
      queryClient.invalidateQueries({ queryKey: CAREER_QUERY_KEYS.biographies });
      // Personen-Detail liefert den Biography-Marker mit → mit aktualisieren
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.persons.detail(personId) });
      setEditing(false);
    },
    onError: (err: Error) => toast.error(err.message || 'Speichern fehlgeschlagen.'),
  });

  const handleSave = async () => {
    if (draft.trim() === '') {
      // Leerer Text löscht den gepflegten Erzähltext – nur nach Rückfrage
      if (!biography) {
        setEditing(false);
        return;
      }
      const ok = await confirm({
        title: 'Lebenslauf-Text löschen?',
        message: (
          <>
            Der Erzähltext von <strong>{displayName}</strong> wird entfernt. Die
            automatisch generierte Chronik bleibt erhalten.
          </>
        ),
        confirmLabel: 'Löschen',
        variant: 'danger',
      });
      if (!ok) return;
      saveMutation.mutate('');
    } else {
      saveMutation.mutate(draft);
    }
  };

  // Name aus dem Personen-Detail (mit Titel); Fallback: Chronik-Person
  const displayName = person
    ? [person.title, person.firstName, person.lastName].filter(Boolean).join(' ')
    : chronicle
      ? [chronicle.person.firstName, chronicle.person.lastName].filter(Boolean).join(' ')
      : '…';

  const lifeDates = person
    ? [
        person.birthDateText ? `* ${person.birthDateText}` : null,
        person.deathDateText
          ? `† ${person.deathDateText}`
          : person.isLiving
            ? 'lebt'
            : null,
      ]
        .filter(Boolean)
        .join(' · ')
    : '';

  // Solange die Kern-Queries nicht entschieden sind (inkl. pausiertem Netz,
  // wo isLoading false bleibt): Spinner statt einer leer wirkenden Biografie.
  if (personQuery.isPending || chronicleQuery.isPending) {
    return (
      <div className="flex justify-center rounded-lg border border-slate-200 bg-white p-12">
        <LoadingSpinner message="Lebenslauf wird geladen..." />
      </div>
    );
  }

  // Ungültige/gelöschte Person (z. B. veralteter ?bio-Deep-Link): klarer
  // Fehlerzustand statt einer plausibel aussehenden leeren Biografie.
  if (personError || chronicleError) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-8 text-center">
        <p className="text-sm font-medium text-slate-700">Person nicht gefunden.</p>
        <p className="mt-1 text-sm text-slate-500">
          Der Link ist ungültig oder die Person wurde inzwischen gelöscht.
        </p>
        <button
          type="button"
          onClick={onBack}
          className="mt-4 rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          Zurück zur Liste
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Kopf: Zurück, Name + Lebensdaten, Stammbaum-Link */}
      <div className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            className="flex-shrink-0 rounded-md border border-slate-300 bg-white p-2 text-slate-600 hover:bg-slate-50"
            aria-label="Zurück zur Liste"
            title="Zurück zur Liste"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          </button>
          <div className="min-w-0">
            <h2 className="truncate text-lg font-semibold text-slate-900">{displayName}</h2>
            {lifeDates && <p className="text-sm text-slate-500">{lifeDates}</p>}
          </div>
        </div>
        <button
          type="button"
          onClick={() => navigate(`/?person=${personId}`)}
          className="flex items-center gap-2 self-start rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 sm:self-auto"
        >
          <ListTree className="h-4 w-4" aria-hidden="true" />
          Im Stammbaum zeigen
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Generierte Chronik */}
        <section className="rounded-lg border border-slate-200 bg-white p-4">
          <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-slate-500">
            <ScrollText className="h-4 w-4" aria-hidden="true" />
            Chronik
          </h3>
          {chronicleLoading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : (
            <ChronicleTimeline events={chronicle?.events ?? []} />
          )}
        </section>

        {/* Erzähltext */}
        <section className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="mb-4 flex items-center justify-between gap-2">
            <h3 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-slate-500">
              <BookOpen className="h-4 w-4" aria-hidden="true" />
              Erzähltext
            </h3>
            {canManage && !editing && !biographyLoading && (
              <button
                type="button"
                onClick={() => {
                  setDraft(biography?.text ?? '');
                  setEditing(true);
                }}
                className="flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
              >
                <Pencil className="h-4 w-4" aria-hidden="true" />
                Bearbeiten
              </button>
            )}
          </div>

          {biographyLoading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : editing ? (
            <div className="space-y-3">
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                rows={14}
                autoFocus
                placeholder="Lebenslauf als Fließtext – Stationen, Anekdoten, Hintergründe ..."
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              />
              <p className="text-xs text-slate-400">
                Ein leerer Text löscht den gepflegten Lebenslauf.
              </p>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setEditing(false)}
                  className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  Abbrechen
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saveMutation.isPending}
                  className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {saveMutation.isPending ? 'Wird gespeichert...' : 'Speichern'}
                </button>
              </div>
            </div>
          ) : biography ? (
            <>
              <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-slate-800">
                {biography.text}
              </p>
              <p className="mt-4 border-t border-slate-100 pt-2 text-xs text-slate-400">
                Zuletzt bearbeitet am{' '}
                {new Date(biography.updatedAt).toLocaleDateString('de-DE')}
              </p>
            </>
          ) : (
            <p className="text-sm text-slate-500">
              Für diese Person ist noch kein Lebenslauf-Text gepflegt.
              {canManage && ' Über „Bearbeiten" kannst du einen anlegen.'}
            </p>
          )}
        </section>
      </div>
    </div>
  );
}
